import React from 'react'
import Image from 'next/image'
import { Markets } from 'context/AppContext'

/**
 * Bu dosya, farklı pazarlar ve diller için ödeme logosu görüntülerini yönetir.
 *
 * Kullanım:
 * 1. İstenen pazar-dil kombinasyonu için ödeme logoları eklemek:
 *    paymentLogos nesnesine yeni bir giriş ekleyin.
 *    Her logo için src, width ve height belirtin.
 *
 * 2. Bir pazar-dil kombinasyonu için ödeme logolarını almak:
 *    const logos = getPaymentLogos('sa', 'ar');
 *
 * 3. Ödeme logoları sadece belirli kombinasyonlarda gösterilir:
 *    - sa-ar
 *    - ae-ar
 *
 * Not: market tr ve az için asla gösterilmez.
 */

interface PaymentLogoConfig {
  src: string
  width: number
  height: number
  alt?: string
  marginTop?: string
  marginLeft?: string
  marginRight?: string
}

const paymentLogos: { [key: string]: PaymentLogoConfig[] } = {
  'sa-ar': [
    {
      src: 'https://cdn2.enuygun.com/media/lib/uploads/image/mada-70677.png',
      width: 88,
      height: 88,
      alt: 'Mada',
    },
    {
      src: 'https://cdn2.enuygun.com/media/lib/uploads/image/visa-70676.png',
      width: 88,
      height: 88,
      alt: 'Visa',
    },
    {
      src: 'https://cdn2.enuygun.com/media/lib/uploads/image/mastercard-70673.png',
      width: 88,
      height: 88,
      alt: 'Mastercard',
    },
    {
      src: 'https://cdn2.enuygun.com/media/lib/uploads/image/applepay-70675.png',
      width: 88,
      height: 88,
      alt: 'Apple Pay',
    },
  ],
  'ae-ar': [
    {
      src: 'https://cdn2.enuygun.com/media/lib/uploads/image/visa-70676.png',
      width: 88,
      height: 88,
      alt: 'Visa',
    },
    {
      src: 'https://cdn2.enuygun.com/media/lib/uploads/image/mastercard-70673.png',
      width: 88,
      height: 88,
      alt: 'Mastercard',
    },
    {
      src: 'https://cdn2.enuygun.com/media/lib/uploads/image/applepay-70675.png',
      width: 88,
      height: 88,
      alt: 'Apple Pay',
    },
    {
      src: 'https://cdn2.enuygun.com/media/lib/uploads/image/gpay-70674.png',
      width: 88,
      height: 88,
      alt: 'Google Pay',
    },
  ],
}

const getPaymentLogos = (market?: Markets, language?: string): React.ReactNode[] | undefined => {
  // market tr ve az için asla gösterilmez
  if (market === 'tr') {
    return undefined
  }

  // market ve language kombinasyonu oluştur
  const key = market && language ? `${market}-${language}` : undefined

  if (!key) {
    return undefined
  }

  const logoConfigs = paymentLogos[key]

  if (!logoConfigs || logoConfigs.length === 0) {
    return undefined
  }

  // Image component'leri olarak render et
  return logoConfigs.map((logo, index) => {
    const altText = logo.alt || `Payment logo ${index + 1}`
    const testId = `payment-logo-${altText.toLowerCase().replaceAll(/\s+/g, '-')}`

    return (
      <Image
        key={logo.alt || logo.src}
        src={logo.src}
        alt={altText}
        width={logo.width}
        height={logo.height}
        style={{ marginTop: logo.marginTop, marginRight: logo.marginRight, marginLeft: logo.marginLeft }}
        unoptimized
        loading="lazy"
        className="payment-logo"
        data-testid={testId}
      />
    )
  })
}

export default getPaymentLogos
